<template>
  <v-row justify="center">
    <v-col cols="12" md="9">

      <!-- Alerta configuración pendiente -->
      <v-alert
        v-if="configuracionPendiente"
        type="warning"
        outlined
        dense
        class="mb-4"
        border="left"
      >
        La empresa tiene configuración pendiente.
        <v-btn
          x-small
          color="warning"
          text
          :to="{ name: 'empresaConfiguracion', params: { uuid_empresa: empresa.uuid } }"
          class="ml-1"
        >Completar ahora</v-btn>
      </v-alert>

      <!-- Header -->
      <v-card outlined class="mb-4">
        <v-card-text class="pb-3">
          <div class="d-flex align-start justify-space-between">
            <div>
              <div class="text-h5 font-weight-bold">{{ empresa.razonSocial }}</div>
              <div class="text-subtitle-1 grey--text">{{ empresa.nombreFantasia }}</div>
              <v-chip x-small outlined color="grey" class="mt-1">
                <v-icon left x-small>mdi-web</v-icon>
                {{ empresa.slug }}.legorsoft.click
              </v-chip>
            </div>
            <v-btn
              small
              color="primary"
              outlined
              @click="setEmpresa(empresa)"
            >
              <v-icon left small>mdi-check-circle-outline</v-icon>
              Usar esta empresa
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Datos -->
      <v-row>
        <!-- Identificación -->
        <v-col cols="12" md="6">
          <v-card outlined height="100%">
            <v-card-title class="text-subtitle-2 pb-1">Identificación</v-card-title>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-card-account-details</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>CUIT</v-list-item-subtitle>
                  <v-list-item-title>{{ empresa.cuit }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-email</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Email</v-list-item-subtitle>
                  <v-list-item-title>{{ empresa.email }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-calendar-plus</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Fecha de alta</v-list-item-subtitle>
                  <v-list-item-title>{{ formatFecha(empresa.fecha_creacion) }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Ubicación -->
        <v-col cols="12" md="6">
          <v-card outlined height="100%">
            <v-card-title class="text-subtitle-2 pb-1">Ubicación</v-card-title>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-map-marker</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Dirección</v-list-item-subtitle>
                  <v-list-item-title>{{ empresa.direccion }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-city</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Localidad</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ empresa.localidad ? empresa.localidad.nombre : '—' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-map</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Provincia</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ empresa.localidad && empresa.localidad.provincia ? empresa.localidad.provincia.nombre : '—' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Configuración fiscal -->
        <v-col cols="12">
          <v-card outlined>
            <v-card-title class="text-subtitle-2 pb-1">
              Configuración fiscal
              <v-spacer></v-spacer>
              <v-btn
                x-small
                text
                color="primary"
                :to="{ name: 'empresaConfiguracion', params: { uuid_empresa: empresa.uuid } }"
              >
                <v-icon left x-small>mdi-pencil</v-icon>
                Editar
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-receipt</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Condición IVA</v-list-item-subtitle>
                  <v-list-item-title>
                    <template v-if="empresa.condicionIva">{{ empresa.condicionIva.nombre }}</template>
                    <v-chip v-else x-small color="warning" outlined>Pendiente</v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-calendar</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Inicio de actividades</v-list-item-subtitle>
                  <v-list-item-title>
                    <template v-if="empresa.fecha_inicio_actividades">{{ formatFecha(empresa.fecha_inicio_actividades) }}</template>
                    <v-chip v-else x-small color="warning" outlined>Pendiente</v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-icon><v-icon small>mdi-briefcase</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-subtitle>Ingresos brutos</v-list-item-subtitle>
                  <v-list-item-title>
                    <template v-if="empresa.ingresobruto">{{ empresa.ingresobruto }}</template>
                    <v-chip v-else x-small color="warning" outlined>Pendiente</v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

    </v-col>
  </v-row>
</template>

<script>
import moment from "moment";

export default {
  name: "EmpresaDetallesGenerales",
  props: {
    empresa: {
      type: Object,
      required: true,
    },
  },
  computed: {
    configuracionPendiente() {
      return !this.empresa.condicionIva || !this.empresa.fecha_inicio_actividades;
    },
  },
  methods: {
    setEmpresa(empresa) {
      this.$store.commit("empresa/SET_EMPRESA", empresa);
      this.$store.commit("auth/SET_EMPRESA", empresa);
    },
    formatFecha(fecha) {
      if (!fecha) return '—';
      return moment(fecha).format("ll");
    },
  },
};
</script>
